<template>
  <div class="pdv-page" data-testid="pdv-page">
    <PDVDashboard ref="dashboardRef" :table-ref="tableRef" data-tutorial="pdv-dashboard" />

    <div style="margin-bottom: 20px; margin-top: 20px; display: flex; gap: 8px; align-items: center;">
      <Button :label="t('Iniciar Nova Venda')" icon="pi pi-plus" size="small" data-testid="btn-iniciar-nova-venda" data-tutorial="btn-nova-venda" @click="handleNovaVenda" />
      <Button
        v-if="lastWhatsAppPayload"
        :label="t('Reenviar WhatsApp')"
        icon="pi pi-whatsapp"
        size="small"
        severity="success"
        data-testid="btn-reenviar-whatsapp"
        @click="handleReenviarWhatsApp"
      />
    </div>

    <PDVTable ref="tableRef" data-tutorial="pdv-table" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import PDVDashboard from './PDVDashboard.vue';
import PDVTable from './PDVTable.vue';
import { log } from '../../utils/DebugLogger';

interface DashboardInstance {
  loadData: () => Promise<void>;
}

interface TableInstance {
  loadData: () => Promise<void>;
  lastLoadedOrders?: { value?: unknown[] } | unknown[];
}

interface OrderCreatedPayload {
  orderId?: number | string;
}

const { t } = useI18n();
const router = useRouter();
const dashboardRef = ref<DashboardInstance | null>(null);
const tableRef = ref<TableInstance | null>(null);

const loadData = async (): Promise<void> => {
  log('PDVPage', 'loadData', 'Reloading data');
  if (tableRef.value) await tableRef.value.loadData();
  if (dashboardRef.value) await dashboardRef.value.loadData();
};

const handleNovaVenda = (): void => {
  log('PDVPage', 'handleNovaVenda', 'Opening client wizard modal');
  eventBus.emit('modal.pdv-cliente-wizard.open', {});
};

const lastWhatsAppPayload = ref<Record<string, unknown> | null>(null);

function handleWhatsAppPayload(payload: Record<string, unknown> | undefined): void {
  if (payload) lastWhatsAppPayload.value = payload;
}

function handleReenviarWhatsApp(): void {
  if (lastWhatsAppPayload.value) {
    eventBus.emit('modal.pdv-enviar-whatsapp.open', lastWhatsAppPayload.value);
  }
}

const handleSaved = (): void => {
  log('PDVPage', 'handleSaved', 'Order saved, reloading data');
  loadData();
};

const handleOrderCreated = (data?: OrderCreatedPayload): void => {
  log('PDVPage', 'handleOrderCreated', 'New order created, opening checkout page', data);
  if (data && data.orderId) {
    router.push('/pdv/checkout/' + data.orderId);
  }
};

onMounted(() => {
  eventBus.on('pdv.saved', handleSaved);
  eventBus.on('pdv.order-created', handleOrderCreated);
  eventBus.on('whatsapp.last-payload', handleWhatsAppPayload);
});

onBeforeUnmount(() => {
  eventBus.off('pdv.saved', handleSaved);
  eventBus.off('pdv.order-created', handleOrderCreated);
  eventBus.off('whatsapp.last-payload', handleWhatsAppPayload);
});

defineExpose({ loadData, handleNovaVenda });
</script>
